// components/UserStats2.js
import React, { useState, useEffect } from 'react';
import { getCurrentUserId } from '../services/database';
import proxyapi from '../services/ProxyApi';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Camera, Clock } from 'lucide-react';

const UserStats2 = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalWireframesProcessed: 0
  });
  const [dailyStats, setDailyStats] = useState([]);
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [view, setView] = useState('total'); // 'total', 'daily', or 'monthly'
  const [dateRange, setDateRange] = useState({
    startDate: getDefaultStartDate(),
    endDate: getDefaultEndDate()
  });
  const [error, setError] = useState(null);

  // Helper functions for default dates
  function getDefaultStartDate() {
    const date = new Date();
    date.setDate(date.getDate() - 30); // Default to last 30 days
    return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
  }

  function getDefaultEndDate() {
    return new Date().toISOString().split('T')[0]; // Today
  }

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const userId = await getCurrentUserId();
        
        if (!userId) {
          setError('User not authenticated');
          setLoading(false);
          return;
        }

        // Fetch total stats
        const totalResponse = await proxyapi.get(`/api/v1/users/${userId}/stats?format=total&start_date=${dateRange.startDate}&end_date=${dateRange.endDate}`);
        console.log('Raw Total Stats API Response:', totalResponse.data);
        
        setStats({
          totalWireframesProcessed: totalResponse.data.data.totalWireframesProcessed || 0
        });

        // Fetch daily stats
        const dailyResponse = await proxyapi.get(`/api/v1/users/${userId}/stats?format=daily&start_date=${dateRange.startDate}&end_date=${dateRange.endDate}`);
        console.log('Raw Daily Stats API Response:', dailyResponse.data);
        
        // Transform daily stats to ensure wireframesProcessed property exists and is properly accessed
        const processedDailyStats = (dailyResponse.data.data.dailyStats || []).map(day => {
          // Check if wireframesProcessed exists directly or is nested
          let wireframesCount = 0;
          
          // Try different possible data structures
          if (day.wireframesProcessed !== undefined) {
            wireframesCount = day.wireframesProcessed;
          } else if (day.viewStats && day.viewStats.wireframe) {
            wireframesCount = day.viewStats.wireframe;
          }
          
          // If the date is missing (unlikely), use a fallback
          const dateValue = day.date || 'Unknown Date';
          
          return {
            ...day,
            date: dateValue,
            wireframesProcessed: wireframesCount
          };
        });

        console.log('Processed Daily Stats:', processedDailyStats);
        setDailyStats(processedDailyStats);

        // Fetch monthly stats
        const monthlyResponse = await proxyapi.get(`/api/v1/users/${userId}/stats?format=monthly&start_date=${dateRange.startDate}&end_date=${dateRange.endDate}`);
        console.log('Raw Monthly Stats API Response:', monthlyResponse.data);
        
        // Transform monthly stats
        const processedMonthlyStats = (monthlyResponse.data.data.monthlyStats || []).map(month => {
          let wireframesCount = 0;
          
          // Try different possible data structures
          if (month.wireframesProcessed !== undefined) {
            wireframesCount = month.wireframesProcessed;
          } else if (month.viewStats && month.viewStats.wireframe) {
            wireframesCount = month.viewStats.wireframe;
          }
          
          // If the month is missing (unlikely), use a fallback
          const monthValue = month.month || 'Unknown Month';
          
          return {
            ...month,
            month: monthValue,
            wireframesProcessed: wireframesCount
          };
        });

        console.log('Processed Monthly Stats:', processedMonthlyStats);
        setMonthlyStats(processedMonthlyStats);

        setError(null);
      } catch (err) {
        console.error('Error fetching wireframe stats:', err);
        setError('Failed to load wireframe statistics. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [dateRange]);

  const handleDateRangeChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({ ...prev, [name]: value }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatMonth = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    return new Date(year, month - 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6 mb-6 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-6 mb-6">
      <h2 className="text-xl font-semibold mb-4 flex items-center">
        <Camera className="mr-2" size={20} />
        Wireframe Usage Statistics
      </h2>

      {error ? (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      ) : (
        <>
          {/* Date Range Selector */}
          <div className="flex flex-wrap items-center mb-6 gap-4">
            <div className="flex items-center">
              <Clock className="mr-2 text-gray-600" size={16} />
              <span className="text-gray-600 mr-2">Date Range:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <input
                type="date"
                name="startDate"
                value={dateRange.startDate}
                onChange={handleDateRangeChange}
                className="border rounded p-1 text-sm"
              />
              <span className="text-gray-600">to</span>
              <input
                type="date"
                name="endDate"
                value={dateRange.endDate}
                onChange={handleDateRangeChange}
                className="border rounded p-1 text-sm"
              />
            </div>
          </div>

          {/* View Selector */}
          <div className="flex mb-6 border-b">
            <button
              onClick={() => setView('total')}
              className={`pb-2 px-4 ${view === 'total' ? 'border-b-2 border-black font-medium' : 'text-gray-500'}`}
            >
              Total
            </button>
            <button
              onClick={() => setView('daily')}
              className={`pb-2 px-4 ${view === 'daily' ? 'border-b-2 border-black font-medium' : 'text-gray-500'}`}
            >
              Daily
            </button>
            <button
              onClick={() => setView('monthly')}
              className={`pb-2 px-4 ${view === 'monthly' ? 'border-b-2 border-black font-medium' : 'text-gray-500'}`}
            >
              Monthly
            </button>
          </div>

          {/* Statistics Display */}
          {view === 'total' && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-lg font-medium text-gray-700 mb-2">Total Wireframes Processed</div>
              <div className="text-3xl font-bold">{stats.totalWireframesProcessed}</div>
            </div>
          )}

          {view === 'daily' && (
            <div>
              <h3 className="text-lg font-medium mb-4">Daily Wireframe Usage</h3>
              {dailyStats.length === 0 || !dailyStats.some(stat => stat.wireframesProcessed > 0) ? (
                <p className="text-gray-500">No daily wireframe statistics available for the selected date range.</p>
              ) : (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={dailyStats}
                      margin={{ top: 20, right: 30, left: 20, bottom: 30 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={formatDate} 
                        angle={-45}
                        textAnchor="end"
                        height={70}
                      />
                      <YAxis allowDecimals={false} />
                      <Tooltip 
                        labelFormatter={date => `Date: ${new Date(date).toLocaleDateString()}`}
                        formatter={(value, name) => {
                          if (name === 'wireframesProcessed') return [value, 'Wireframes'];
                          return [value, name]; // Fallback
                        }}
                      />
                      <Legend />
                      <Bar dataKey="wireframesProcessed" name="Wireframes" fill="#8884d8" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}

          {view === 'monthly' && (
            <div>
              <h3 className="text-lg font-medium mb-4">Monthly Wireframe Usage</h3>
              {monthlyStats.length === 0 || !monthlyStats.some(stat => stat.wireframesProcessed > 0) ? (
                <p className="text-gray-500">No monthly wireframe statistics available for the selected date range.</p>
              ) : (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={monthlyStats}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="month" 
                        tickFormatter={formatMonth}
                      />
                      <YAxis allowDecimals={false} />
                      <Tooltip 
                        labelFormatter={month => `Month: ${formatMonth(month)}`}
                        formatter={(value, name) => {
                          if (name === 'wireframesProcessed') return [value, 'Wireframes'];
                          return [value, name]; // Fallback
                        }}
                      />
                      <Legend />
                      <Bar dataKey="wireframesProcessed" name="Wireframes" fill="#8884d8" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}

          <div className="mt-4 text-xs text-gray-500">
            <p><Clock className="inline mr-1" size={12} /> Statistics updated as of {new Date().toLocaleString()}</p>
          </div>
        </>
      )}
    </div>
  );
};

export default UserStats2;